// @flow
import * as React from 'react';

import clsx from 'clsx';
import { ArrowRight, CircleX, Trash, File } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { selectIsAuthenticatedUser } from '@gandi/authentication';
import { useTranslator } from '@gandi/translate';
import { Button, Choice, Drawer, useDrawerState, Heading } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { reset as resetFilter } from '../../DomainCreate/modules/actions/filters';

import { selectSearchValue } from '../../DomainCreate/modules/selectors/search';
import { DomainCount } from '../../DomainTransferBulkResult/components/DomainCount';
import useDebounce from '../../helpers/use-debounce';

import { CSVUploader } from './CSVUploader';

import { LoginBlock } from './LoginBlock';
import { useDragAndDrop } from './useDragAndDrop';

import styles from './BulkInput.scss';

type Process = 'create' | 'renew' | 'transfer';

function Title({ process }: { process: Process }): string {
  const { __ } = useTranslator();
  switch (process) {
    case 'renew':
      return __('Multiple renew interface');
    case 'transfer':
      return __('Multiple transfer interface');
    default:
      // create
      return __('Multiple search interface');
  }
}

function scrollTo(hash: string) {
  document.querySelector(`#${hash}`)?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
    inline: 'start',
  });
}

/** Trim search value and normalize domain separators to be either
 *  spaces or line breaks.
 */
export function normalize(search: string, separator: '\n' | ' '): string {
  return search
    .split(/\s+/)
    .filter(d => d.trim() !== '')
    .join(separator);
}

/** Remove surrounding quotes, commas and spaces, and return the first field only
 *  (drop everything after the first possible separator). */
function stripQuotes(v: string): string {
  return v.replace(/^\s*["']?|["']?\s*(?:[,;].*)?$/g, '');
}

/** Return domains only, ignoring CSV quotes and separators. Accept IDN. */
export function validateDomains(values: Array<string>): Array<string> {
  const domainPattern = /^(?:[\p{L}\p{N}-]+\.)+[\p{L}]{2,}$/u;
  return values.map(stripQuotes).filter(value => domainPattern.test(value));
}

function downloadCSV(): void {
  const csv = 'fqdn;authcode';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', 'template_transfer.csv');
  link.style.visibility = 'hidden';
  document.body?.appendChild(link);
  link.click();
  document.body?.removeChild(link);
}

function ChoiceOption({
  value,
  label,
  isActive,
  onChange,
  children,
}: {
  value: 'manually' | 'import',
  label: string,
  isActive: boolean,
  onChange: (evt: SyntheticInputEvent<>) => void,
  children: React.Node,
}) {
  return (
    <div className={styles.choiceContainer}>
      <Choice
        name="choice"
        className={styles.choice}
        label={label}
        value={value}
        onChange={onChange}
        checked={isActive}
      />
      {isActive && children}
    </div>
  );
}

export function BulkInput({
  process,
  submitSearch,
}: {
  process: 'create' | 'renew' | 'transfer',
  submitSearch: string => mixed,
}): React.MixedElement {
  const { __ } = useTranslator();
  const dispatch = useDispatch();

  const [searchParams, setSearchParams] = useSearchParams();

  const search = useAppSelector(selectSearchValue);

  const drawer = useDrawerState();

  const isAuthenticated = useAppSelector(selectIsAuthenticatedUser);

  const [userInput, setUserInput] = React.useState('');
  const [searchSubmitted, setSearchSubmitted] = React.useState(false);
  const [domainValid, setDomainValid] = React.useState<Array<string>>([]);
  const [fileName, setFileName] = React.useState('');
  const [error, setError] = React.useState('');

  const debouncedSearchTerm = useDebounce(userInput, 300);

  const choiceValue = searchParams.get('choice');
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

  const { bind, isDragging } = useDragAndDrop({
    setDomains: setUserInput,
    setFileName,
  });

  const count = domainValid.length;

  React.useEffect(() => {
    const normalizedValue = debouncedSearchTerm
      .split(/\s+/)
      .filter(d => d.trim() !== '');
    const currentValidDomains = validateDomains(normalizedValue);

    if (normalizedValue && currentValidDomains.length) {
      setDomainValid(currentValidDomains);
    } else {
      setDomainValid([]);
    }
  }, [debouncedSearchTerm]);

  // Init
  React.useEffect(() => {
    textareaRef.current?.focus();
    // $FlowFixMe[not-an-array]
    dispatch(resetFilter());

    // Set initial value when reloading the page
    if (textareaRef.current?.value) {
      setUserInput(textareaRef.current.value);
    }
  }, [dispatch, choiceValue]);

  React.useEffect(() => {
    if (count) {
      drawer.show();
    } else {
      drawer.close();
    }
  }, [count, drawer]);

  function handleChange(evt: SyntheticEvent<HTMLTextAreaElement>) {
    const target = evt.currentTarget;
    const value = target.value;
    setUserInput(value);
  }

  const handleSearch = () => {
    // Replace spaces with line breaks in the field
    setUserInput(normalize(userInput, '\n'));

    // Replace line breaks with spaces for the search
    const apiSearch = normalize(userInput, ' ');

    submitSearch(apiSearch);
    setSearchSubmitted(true);
  };

  function handleRemove() {
    setUserInput('');
    setFileName('');
  }

  // Scroll every time a new search is made
  // (we don't do it in handleSearch because `#results` is not mounted yet).
  // Only in the Register tab for now as it's the only one with #results.
  // In the future, we may use a ref to the results instead of a `search` selector.
  React.useEffect(() => {
    if (search && searchSubmitted) {
      scrollTo('results');
      setSearchSubmitted(false);
    }
  }, [search, searchSubmitted]);

  // KeyDown event handler used to submit the bulk search form on Ctrl + Enter or Cmd + Enter.
  function handleTextareaKeyDown(evt: SyntheticKeyboardEvent<>) {
    if (evt.key === 'Enter' && (evt.ctrlKey || evt.metaKey)) {
      handleSearch();
    }
  }

  function handleSelectChoice(evt: SyntheticInputEvent<>) {
    searchParams.set('choice', evt.target.value);
    setSearchParams(searchParams);
    setError('');
    setFileName('');
  }

  const textarea = (
    // eslint-disable-next-line @gandi/gandi/no-global-element
    <textarea
      {...bind}
      ref={textareaRef}
      value={userInput}
      onChange={handleChange}
      rows={5}
      onKeyDown={handleTextareaKeyDown}
      placeholder={__('Domain name\nDomain name\nDomain name\nDomain name')}
      className={isDragging ? styles.isDragging : undefined}
    />
  );

  const choices = [
    {
      value: 'manually',
      label: __('Add domain names manually'),
      content: textarea,
    },
    {
      value: 'import',
      label: __('Import CSV files'),
      content: (
        <>
          <Button
            className={styles.downloadCSV}
            kind="link"
            type="button"
            onClick={downloadCSV}
          >
            {__('Download our .CSV file template')}
          </Button>

          {fileName ? (
            <div className={styles.fileBlock}>
              <span>
                <File size={24} />
                {fileName}
              </span>
              <Button type="button" onClick={handleRemove} kind="ghost">
                <Trash size={16} />
              </Button>
            </div>
          ) : (
            <>
              {error && (
                <span className={styles.errorContainer}>
                  <CircleX size={16} />
                  {error}
                </span>
              )}
              <CSVUploader
                setError={setError}
                setFileName={setFileName}
                setDomains={setUserInput}
              />
            </>
          )}
        </>
      ),
    },
  ];

  const displayBulk = process === 'transfer';

  const content = displayBulk ? (
    <div className={styles.choicesContainer}>
      {choices.map(choice => (
        <ChoiceOption
          key={choice.value}
          value={choice.value}
          label={choice.label}
          isActive={choiceValue === choice.value}
          onChange={handleSelectChoice}
        >
          {choice.content}
        </ChoiceOption>
      ))}
    </div>
  ) : (
    textarea
  );

  function handleReset() {
    setUserInput('');
    textareaRef.current?.focus();
  }

  const requireLogin = !isAuthenticated;

  return (
    <div className={styles.root}>
      <div
        className={clsx(styles.content, requireLogin ? styles.blur : undefined)}
        inert={requireLogin}
      >
        <div className={styles.titleContainer}>
          <Heading as="h3" fontSize="l">
            <Title process={process} />
          </Heading>
          {displayBulk && (
            <span>{__('Select a method to transfer your domain names')}</span>
          )}

          <span className={styles.subtitleContainer}>
            <DomainCount count={count} />
            {!!userInput && (!displayBulk || choiceValue === 'manually') && (
              <Button type="button" kind="link" onClick={handleReset}>
                {__('Reset search')}
              </Button>
            )}
          </span>
        </div>

        {content}

        {!displayBulk && (
          <div className={styles.containerButtons}>
            <Button
              type="button"
              onClick={handleSearch}
              block
              disabled={!count}
            >
              {__('Search')}
            </Button>
          </div>
        )}
      </div>

      {requireLogin && <LoginBlock />}

      {displayBulk && (
        <Drawer {...drawer} position="bottom">
          <div className={styles.drawerButton}>
            <Button type="button" onClick={handleSearch}>
              {__('Domain names verification')}
              <ArrowRight size={20} />
            </Button>
          </div>
        </Drawer>
      )}
    </div>
  );
}
